import { Component, Input, OnInit, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { NetworkStatusService } from '@app/core/services/network-status.service';
import { AppService, ApplicationMode } from '@app/core/services/app.service';
import { Router } from '@angular/router';
import { ChangeLogService, Subscription } from '@app/core/services/change-log.service';
import { BroadcastChannel } from 'broadcast-channel';
import { Subscription as RxSubscription, firstValueFrom } from 'rxjs';
import { ChangeLogEvent } from '@app/core/db/change-log';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmComponent } from '@app/shared/confirm/confirm.component';
import { LoaderService } from '@app/header/loader/loader.service';
@Component({
  selector: 'app-offline-toolbar',
  templateUrl: './offline-toolbar.component.html',
  styleUrls: ['./offline-toolbar.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class OfflineToolbarComponent implements OnInit, OnDestroy {
  @Input() showOfflineControls = true;
  online = true;
  offlineChanges = 0;
  rejectedChanges = 0;
  reviewing = false;
  rejectedItems: ChangeLogEvent[] = [];
  downloadedChangeKeys = new Set<string>();
  discarding = false;
  resolutionMessage = '';
  private readonly syncMessageHandler = () => this.onSyncMessage();
  syncChannel: BroadcastChannel;
  changeSubscription: Subscription;
  subscriptions: RxSubscription;

  constructor(
    private networkStatusService: NetworkStatusService,
    private changeLogServices: ChangeLogService,
    private appService: AppService,
    private router: Router,
    private dialog: MatDialog,
    private translate: TranslateService,
    public loaderService: LoaderService,
  ) {
    this.syncChannel = new BroadcastChannel('dwo-sync');
    this.subscriptions = new RxSubscription();
  }

  async ngOnInit(): Promise<void> {
    this.subscriptions.add(
      this.networkStatusService.isOnline().subscribe((online) => {
        this.online = online;
      }),
    );

    this.syncChannel.addEventListener('message', this.syncMessageHandler);
    this.subscriptions.add(this.changeLogServices.changes$.subscribe(() => void this.fetchPendingCount()));

    this.changeSubscription = this.changeLogServices.subscribeAll(async () => {
      await this.fetchPendingCount();
    });

    await this.fetchPendingCount();
  }

  ngOnDestroy(): void {
    this.syncChannel.removeEventListener('message', this.syncMessageHandler);
    void this.syncChannel.close();
    this.changeSubscription.dispose();
    this.subscriptions.unsubscribe();
  }

  onSyncMessage() {
    this.fetchPendingCount();
  }

  async fetchPendingCount() {
    const [pending, rejected] = await Promise.all([
      this.changeLogServices.getPendingCount(),
      this.changeLogServices.getRejectedCount(),
    ]);
    this.offlineChanges = pending;
    this.rejectedChanges = rejected;
    if (this.reviewing) {
      this.rejectedItems = await this.changeLogServices.getRejected();
      const downloaded = await Promise.all(
        this.rejectedItems.map(async (change) =>
          (await this.changeLogServices.hasDownloadedData(change.refs)) ? change.key : null,
        ),
      );
      this.downloadedChangeKeys = new Set(downloaded.filter((key) => key !== null));
    }
  }

  async toggleReview(): Promise<void> {
    this.reviewing = !this.reviewing;
    await this.fetchPendingCount();
  }

  async discard(change: ChangeLogEvent): Promise<void> {
    if (
      this.discarding ||
      !this.online ||
      !this.loaderService.isBackendReachable ||
      this.downloadedChangeKeys.has(change.key)
    )
      return;
    this.discarding = true;
    this.resolutionMessage = '';
    try {
      const confirmed = await firstValueFrom(
        this.dialog
          .open(ConfirmComponent, {
            maxWidth: '90vw',
            autoFocus: 'dialog',
            data: {
              message: this.translate.instant('app.offlineToolbar.discardTitle'),
              confirmMessage: this.translate.instant('app.offlineToolbar.discardConfirmation'),
              icon: 'delete',
            },
          })
          .afterClosed(),
      );
      if (confirmed !== true) return;
      this.resolutionMessage = (await this.changeLogServices.discardRejected(change))
        ? 'app.offlineToolbar.discarded'
        : 'app.offlineToolbar.discardFailed';
      await this.fetchPendingCount();
    } catch {
      this.resolutionMessage = 'app.offlineToolbar.discardFailed';
    } finally {
      this.discarding = false;
    }
  }

  switchToOnline() {
    this.appService.setMode(ApplicationMode.ONLINE);
    this.router.navigateByUrl('/');
  }
}
